import type { SendWhatsappTemplatePayload } from "@/types/whatsapp/send-template";

import { DateTime } from "luxon";

import { getMockMarketProfile } from "../market";

import { createPastTimestamp } from "./base";

import {
  ResolvedWhatsappTemplatePayload,
  WHATSAPP_TEMPLATE_BUTTON_TYPE,
  WHATSAPP_TEMPLATE_COMPONENT_TYPE,
  WHATSAPP_TEMPLATE_HEADER_FORMAT,
  WhatsappTemplate,
  WhatsappTemplateComponent,
} from "@/types/whatsapp/template";

type ClinicTemplateSeed = Pick<
  WhatsappTemplate,
  "name" | "category" | "components"
> &
  Partial<
    Pick<
      WhatsappTemplate,
      | "language"
      | "parameterFormat"
      | "status"
      | "qualityScore"
      | "rejectedReason"
      | "rejectionInfo"
    >
  > & {
    /** Days since Meta last reviewed it; 0 is today. */
    updatedDaysAgo: number;
  };

/**
 * Templates a clinic would keep on file across its two WhatsApp numbers. Most
 * are approved; one is pending and one rejected, so the list shows every state.
 */
function clinicTemplateSeeds(): ClinicTemplateSeed[][] {
  const profile = getMockMarketProfile();

  return [
    [
      {
        name: "appointment_reminder_day_before",
        category: "utility",
        parameterFormat: "named",
        updatedDaysAgo: 19,
        components: [
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.HEADER,
            format: WHATSAPP_TEMPLATE_HEADER_FORMAT.TEXT,
            text: "See you tomorrow",
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BODY,
            text: "Hi {{first_name}}, your appointment with {{doctor_name}} is tomorrow at {{appointment_time}}.\n\nPlease arrive 10 minutes early so we can start on time. Can you confirm you're coming?",
            example: {
              bodyTextNamedParams: [
                { paramName: "first_name", example: "Sara" },
                { paramName: "doctor_name", example: "Dr. Lina Haddad" },
                { paramName: "appointment_time", example: "4:30 PM" },
              ],
            },
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.FOOTER,
            text: profile.organizationName,
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BUTTONS,
            buttons: [
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.QUICK_REPLY,
                text: "Yes, I'll be there",
              },
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.QUICK_REPLY,
                text: "I need to reschedule",
              },
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.URL,
                text: "Book online",
                url: `${profile.organizationWebsite}/book`,
              },
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.PHONE_NUMBER,
                text: "Call the clinic",
                phoneNumber: "+97141234567",
              },
            ],
          },
        ],
      },
      {
        name: "appointment_confirmation",
        category: "utility",
        updatedDaysAgo: 19,
        components: [
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.HEADER,
            format: WHATSAPP_TEMPLATE_HEADER_FORMAT.TEXT,
            text: "Appointment Confirmed ✅",
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BODY,
            text: "Hi {{1}}, your appointment with {{2}} is confirmed for {{3}}.\n\nPlease arrive 10 minutes early. Reply here if you need to make any changes.",
            example: {
              bodyText: ["Sarah", "Dr. Patel", "Mon, 12 Feb at 10:30 AM"],
            },
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.FOOTER,
            text: profile.organizationName,
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BUTTONS,
            buttons: [
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.QUICK_REPLY,
                text: "Confirm",
              },
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.QUICK_REPLY,
                text: "Reschedule",
              },
            ],
          },
        ],
      },
      {
        name: "appointment_reminder_three_days",
        category: "utility",
        updatedDaysAgo: 19,
        components: [
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.HEADER,
            format: WHATSAPP_TEMPLATE_HEADER_FORMAT.TEXT,
            text: "Appointment Reminder",
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BODY,
            text: "Hi {{1}}, this is a reminder for your appointment on {{2}} with {{3}}. Reply CONFIRM to keep your slot or RESCHEDULE to change it.",
            example: {
              bodyText: ["Sarah", "Thursday at 10:30 AM", "Dr. Patel"],
            },
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.FOOTER,
            text: profile.organizationName,
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BUTTONS,
            buttons: [
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.QUICK_REPLY,
                text: "Confirm",
              },
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.PHONE_NUMBER,
                text: "Call clinic",
                phoneNumber: "+14155550123",
              },
            ],
          },
        ],
      },
      {
        name: "lead_intro",
        category: "marketing",
        qualityScore: "yellow",
        updatedDaysAgo: 28,
        components: [
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.HEADER,
            format: WHATSAPP_TEMPLATE_HEADER_FORMAT.TEXT,
            text: `Welcome to ${profile.organizationName}`,
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BODY,
            text: "Hi {{1}}, thanks for reaching out. Save this number to reach us anytime for appointments, reminders and questions.",
            example: {
              bodyText: ["Sarah"],
            },
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BUTTONS,
            buttons: [
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.URL,
                text: "Book appointment",
                url: `${profile.organizationWebsite}/book`,
              },
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.QUICK_REPLY,
                text: "Talk to us",
              },
            ],
          },
        ],
      },
      {
        name: "lead_intro",
        category: "marketing",
        language: "ar",
        status: "pending",
        qualityScore: "unknown",
        updatedDaysAgo: 0,
        components: [
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BODY,
            text: "مرحباً {{1}}، شكراً لتواصلك معنا. احفظ هذا الرقم لتصل إلينا في أي وقت للمواعيد والتذكيرات والأسئلة.",
            example: {
              bodyText: ["سارة"],
            },
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BUTTONS,
            buttons: [
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.QUICK_REPLY,
                text: "تحدث معنا",
              },
            ],
          },
        ],
      },
      {
        name: "no_show_openings",
        category: "marketing",
        status: "rejected",
        qualityScore: "unknown",
        rejectedReason: "INCORRECT_CATEGORY",
        rejectionInfo: {
          reason:
            "The wording promotes an offer, so it can't be filed as Utility.",
          recommendation: "Duplicate it, fix the text and submit again.",
        },
        updatedDaysAgo: 41,
        components: [
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BODY,
            text: "Hi {{1}}, we missed you today. Book this week and get 20% off your next visit.",
            example: {
              bodyText: ["Sarah"],
            },
          },
        ],
      },
    ],
    [
      {
        name: "recall_invite",
        category: "marketing",
        updatedDaysAgo: 34,
        components: [
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BODY,
            text: "Hi {{1}}, it's been six months since your last check-up. Would you like us to book your next one?",
            example: {
              bodyText: ["Sarah"],
            },
          },
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BUTTONS,
            buttons: [
              {
                type: WHATSAPP_TEMPLATE_BUTTON_TYPE.QUICK_REPLY,
                text: "Book me in",
              },
            ],
          },
        ],
      },
      {
        name: "consult_reviews",
        category: "marketing",
        language: "ar",
        updatedDaysAgo: 48,
        components: [
          {
            type: WHATSAPP_TEMPLATE_COMPONENT_TYPE.BODY,
            text: "مرحباً {{1}}، يسعدنا رأيك في زيارتك الأخيرة. هل تشاركنا تقييمك؟",
            example: {
              bodyText: ["سارة"],
            },
          },
        ],
      },
    ],
  ];
}

/** `numberIndex` is the channel's place among the workspace's WhatsApp numbers. */
export function generateClinicWhatsappTemplates(
  channelId: string,
  numberIndex = 0,
): WhatsappTemplate[] {
  const seeds = clinicTemplateSeeds();
  const offset = seeds
    .slice(0, numberIndex)
    .reduce((count, group) => count + group.length, 0);

  return (seeds[numberIndex] ?? []).map(
    ({ updatedDaysAgo, ...seed }, index) => {
      const updatedAt = DateTime.now().minus({ days: updatedDaysAgo }).toISO();

      return {
        id: `template-${offset + index + 1}`,
        externalId: `mock-external-${offset + index + 1}`,
        channelId,
        organizationId: "mock-org-id",
        workspaceId: "mock-workspace-id",
        parameterFormat: "positional",
        language: "en",
        status: "approved",
        qualityScore: "green",
        qualityScoreDate: updatedAt,
        createdAt: updatedAt,
        updatedAt,
        lastEditedAt: null,
        createdBy: "mock-user-id",
        lastEditedBy: "mock-user-id",
        deletedAt: null,
        lastUsedAt: index === 0 ? createPastTimestamp(1) : null,
        ...seed,
      };
    },
  );
}

const PLACEHOLDER_REGEX = /\{\{\s*(\w+)\s*\}\}/g;
const HAS_PLACEHOLDER_REGEX = /\{\{\s*\w+\s*\}\}/;

type TemplateVarEntry = { text?: string; paramName?: string };

/**
 * Substitutes {{1}}/{{name}} placeholders with the values the agent filled in.
 * Positional tokens map by 1-based index, named tokens by paramName. Unfilled
 * placeholders are left intact so the message still reads sensibly.
 */
function applyVariables(text: string, entries: TemplateVarEntry[]): string {
  return text.replace(PLACEHOLDER_REGEX, (full, token: string) => {
    if (/^\d+$/.test(token)) {
      return entries[parseInt(token, 10) - 1]?.text ?? full;
    }

    return entries.find((entry) => entry.paramName === token)?.text ?? full;
  });
}

/**
 * Resolves a template + the agent-supplied variables into the components shape
 * the conversation renderer expects (placeholders already substituted). This is
 * the mock stand-in for the backend's template resolution.
 */
export function resolveWhatsappTemplatePayload(
  template: WhatsappTemplate,
  variables: SendWhatsappTemplatePayload,
): ResolvedWhatsappTemplatePayload {
  const bodyEntries = variables.body ?? [];
  const headerEntries = variables.header ?? [];
  const buttonEntries = variables.buttons ?? [];
  let urlButtonCursor = 0;

  const templatePayload: WhatsappTemplateComponent[] = template.components.map(
    (component) => {
      if (component.type === WHATSAPP_TEMPLATE_COMPONENT_TYPE.HEADER) {
        if (component.format === WHATSAPP_TEMPLATE_HEADER_FORMAT.TEXT) {
          return {
            ...component,
            text: applyVariables(component.text ?? "", headerEntries),
          };
        }

        const media = headerEntries.find((entry) => entry.link);

        return media?.link ? { ...component, link: media.link } : component;
      }

      if (component.type === WHATSAPP_TEMPLATE_COMPONENT_TYPE.BODY) {
        return {
          ...component,
          text: applyVariables(component.text, bodyEntries),
        };
      }

      if (component.type === WHATSAPP_TEMPLATE_COMPONENT_TYPE.BUTTONS) {
        return {
          ...component,
          buttons: component.buttons.map((button) => {
            if (
              button.type === WHATSAPP_TEMPLATE_BUTTON_TYPE.URL &&
              HAS_PLACEHOLDER_REGEX.test(button.url)
            ) {
              const value = buttonEntries[urlButtonCursor++];

              return {
                ...button,
                url: applyVariables(button.url, value ? [value] : []),
              };
            }

            return button;
          }),
        };
      }

      return component;
    },
  );

  return { templatePayload, deliveryPayload: [] };
}
